/**
 * Pure filter-rule logic shared by Stats and History.
 *
 * Extracted from FilterPanel.jsx (H8/H9 hardening) so the rule parsing,
 * API-param building, and client-side matching can be unit-tested in
 * isolation and reused without pulling in React/DOM. NO React imports here.
 *
 * Exports:
 *   FILTER_DIMS              — full dimension config array
 *   TODAY, DATE_MIN         — date bounds for the date inputs
 *   DATE_RELOPS             — relative date-range operators needing no value
 *   YEAR_OPTIONS            — selectable year strings
 *   YEAR_MONTH_OPTIONS      — selectable "YYYY-MM" strings
 *   parseRules(sp)          — parse URLSearchParams → [{dim,op,val}]
 *   expandDateRange(op,val) — date-range op/val → backend date params
 *   buildApiParams(sp)      — rules → backend query params object
 *   getOperators(def)       — operator list for a dimension def
 *   rulePillLabel(...)      — human label for a rule pill
 *
 * There is deliberately NO client-side round matcher here. A `matchesRules()`
 * used to live alongside buildApiParams and re-implement every dimension for
 * History, which meant the same saved filter had two definitions — and they
 * disagreed on three edges (#2049): it fell back to `started_at` when
 * `completed_at` was null, dropped rounds with a null distance, and skipped
 * `bow_style`/`loadout` entirely because they aren't on RoundSummary. Since
 * #2029 History pages through /api/rounds/page with buildApiParams output, so
 * the matcher had no caller; it is gone and the backend
 * (`app/round_filters.py`) is the only thing that evaluates a filter rule.
 * Anything History still narrows on the client (session_kind) is a dimension
 * the server has no concept of, not a second copy of a server rule.
 */
import { BOW_STYLE_OPTIONS } from "./bowStyles";
import { localISODate } from "./dates";
import { FACE_LABEL } from "./roundConstants";

// ─── Constants ────────────────────────────────────────────────────────────────

// The archer's local today, NOT the UTC one (#2160). This is the `max` bound
// on the FilterPanel date inputs, so a UTC day east of Greenwich would refuse
// today's date for the ~12 hours between local midnight and the UTC rollover.
export const TODAY    = localISODate();
export const DATE_MIN = "2026-01-01";

// Relative date-range operators that need no explicit from/to value
// (shared by the FilterPanel value editors and the client-side matcher below).
export const DATE_RELOPS = ["this_week", "this_month", "this_quarter", "this_year"];

export const YEAR_OPTIONS = (() => {
  const start = parseInt(DATE_MIN.split("-")[0], 10);
  const end   = new Date().getFullYear();
  const opts  = [];
  for (let y = start; y <= end; y++) opts.push(String(y));
  return opts;
})();

export const YEAR_MONTH_OPTIONS = (() => {
  const [sy, sm] = DATE_MIN.split("-").map(Number);
  const now = new Date();
  const ey = now.getFullYear(), em = now.getMonth() + 1;
  const opts = [];
  let y = sy, m = sm;
  while (y < ey || (y === ey && m <= em)) {
    opts.push(`${y}-${String(m).padStart(2, "0")}`);
    if (++m > 12) { m = 1; y++; }
  }
  return opts;
})();

export const FILTER_DIMS = [
  { id: "kind",        label: "Kind",         valueType: "multi-pill",
    options: [{ value: "practice", label: "Practice" }, { value: "league", label: "League" }, { value: "tournament", label: "Tourney" }] },
  { id: "location",    label: "Location",     valueType: "multi-pill",
    options: [{ value: "indoor", label: "Indoor" }, { value: "outdoor", label: "Outdoor" }] },
  { id: "face",        label: "Target face",  valueType: "face" },
  // Options come from shared/enums.json via lib/bowStyles.js (#2048). This
  // dimension used to hold its own copy in a different order (barebow before
  // compound); the shared order now wins.
  { id: "bow_style",   label: "Bow style",    valueType: "select",
    options: BOW_STYLE_OPTIONS },
  { id: "tags",        label: "Tags",         valueType: "tags" },
  { id: "date_range",  label: "Date range",   valueType: "daterange" },
  { id: "distance",    label: "Distance (m)", valueType: "number" },
  { id: "score_range", label: "Score range",  valueType: "numrange" },
  { id: "ends_range",  label: "Ends range",   valueType: "numrange" },
  // Arrows per end (#2029). Backed by the same RoundFilterParams dimension the
  // Stats routes have always had; History reached it only once /api/rounds*
  // stopped re-declaring its own filter block. The backend matches this one by
  // EQUALITY only (round_filter_clauses takes no operator for it), so the
  // operator list is pinned to "equals" rather than offering comparisons the
  // server would silently ignore.
  { id: "arrows_per_end", label: "Arrows per end", valueType: "number",
    operators: [{ value: "eq", label: "equals" }] },
];

// ─── URL state helpers ─────────────────────────────────────────────────────────

export function parseRules(sp) {
  return sp.getAll("r").map((raw) => {
    const [dim, op, ...rest] = raw.split(":");
    return { dim, op, val: rest.join(":") };
  });
}

// Period bounds below are LOCAL calendar days end to end (#2160). They used to
// run through a private `isoDate()` that called toISOString(), mixing frames:
// `new Date(y, m, 1)` is local midnight on the 1st, which east of Greenwich is
// still the last day of the PREVIOUS month in UTC — so "this month" leaked a
// day of last month, and `date_to` was yesterday. localISODate keeps both ends
// in the archer's frame; there is no second implementation of it any more.
export function expandDateRange(op, val) {
  const now = new Date();
  if (op === "between") {
    const [from, to] = val.split("|");
    return {
      ...(from ? { date_from: from, date_from_op: "gte" } : {}),
      ...(to   ? { date_to:   to,   date_to_op:   "lte" } : {}),
    };
  }
  if (op === "year") {
    return { date_from: `${val}-01-01`, date_from_op: "gte", date_to: `${val}-12-31`, date_to_op: "lte" };
  }
  if (op === "month") {
    const [yr, mo] = val.split("-").map(Number);
    const last = new Date(yr, mo, 0).getDate();
    return { date_from: `${val}-01`, date_from_op: "gte", date_to: `${val}-${String(last).padStart(2,"0")}`, date_to_op: "lte" };
  }
  if (op === "this_week") {
    const d = new Date(now); const dow = d.getDay() || 7;
    d.setDate(d.getDate() - dow + 1);
    return { date_from: localISODate(d), date_from_op: "gte", date_to: localISODate(now), date_to_op: "lte" };
  }
  if (op === "this_month") {
    return { date_from: localISODate(new Date(now.getFullYear(), now.getMonth(), 1)), date_from_op: "gte", date_to: localISODate(now), date_to_op: "lte" };
  }
  if (op === "this_quarter") {
    const q = Math.floor(now.getMonth() / 3);
    return { date_from: localISODate(new Date(now.getFullYear(), q * 3, 1)), date_from_op: "gte", date_to: localISODate(now), date_to_op: "lte" };
  }
  if (op === "this_year") {
    return { date_from: `${now.getFullYear()}-01-01`, date_from_op: "gte", date_to: localISODate(now), date_to_op: "lte" };
  }
  return {};
}

export function buildApiParams(sp) {
  const p = {};
  for (const { dim, op, val } of parseRules(sp)) {
    if (dim === "kind")        { if (val) { p.kind       = val; p.kind_op       = op; } }
    if (dim === "location")    { if (val) { p.location   = val; p.location_op   = op; } }
    if (dim === "face")        { if (val) { p.faces      = val.split(","); p.faces_op = op; } }
    if (dim === "bow_style")   { if (val) { p.bow_style  = val; p.bow_style_op  = op; } }
    if (dim === "loadout")     { if (val) { p.loadout_id = Number(val); p.loadout_op = op; } }
    if (dim === "tags")        { if (val) { p.tag_ids    = val; p.tag_op = op; } }
    if (dim === "date_range")  { Object.assign(p, expandDateRange(op, val)); }
    if (dim === "score_range") {
      const [from, to] = val.split("|");
      if (from) { p.score_min = Number(from); p.score_min_op = "gte"; }
      if (to)   { p.score_max = Number(to);   p.score_max_op = "lte"; }
    }
    if (dim === "ends_range")  {
      const [from, to] = val.split("|");
      if (from) { p.ends_min = Number(from); p.ends_min_op = "gte"; }
      if (to)   { p.ends_max = Number(to);   p.ends_max_op = "lte"; }
    }
    if (dim === "distance")    { if (val) { p.distance_m = Number(val); p.distance_op = op; } }
    if (dim === "arrows_per_end") { if (val) { p.arrows_per_end = Number(val); } }
    if (dim === "participation") { if (val) { p.participation = val; } }
  }
  return p;
}

// ─── Operator config ───────────────────────────────────────────────────────────

export function getOperators(def) {
  if (!def) return [{ value: "in", label: "is" }];
  // A dimension may pin its own operator list when the backend supports fewer
  // comparisons than its valueType would normally imply (e.g. arrows_per_end,
  // which the server matches by equality only).
  if (def.operators) return def.operators;
  if (def.valueType === "tags") return [
    { value: "any",  label: "includes any of" },
    { value: "all",  label: "includes all of" },
    { value: "none", label: "does not include any of" },
  ];
  if (def.valueType === "daterange") return [
    { value: "between",      label: "is between" },
    { value: "year",         label: "year is" },
    { value: "month",        label: "year & month is" },
    { value: "this_week",    label: "this week" },
    { value: "this_month",   label: "this month" },
    { value: "this_quarter", label: "this quarter" },
    { value: "this_year",    label: "this year" },
  ];
  if (def.valueType === "numrange") return [{ value: "range", label: "is between" }];
  if (def.valueType === "number") return [
    { value: "eq",  label: "equals" },
    { value: "ne",  label: "not equals" },
    { value: "gt",  label: "greater than" },
    { value: "lt",  label: "less than" },
    { value: "gte", label: "≥ or equal" },
    { value: "lte", label: "≤ or equal" },
  ];
  return [
    { value: "in",     label: "is" },
    { value: "not_in", label: "is not" },
  ];
}

// ─── Rule pill label ───────────────────────────────────────────────────────────

export function rulePillLabel(dim, val, op, tags, dims = FILTER_DIMS) {
  const def   = dims.find((d) => d.id === dim);
  const label = def?.label ?? dim;
  if (dim === "face") {
    return `${label} = ${val.split(",").map((f) => FACE_LABEL[f] ?? f).join(", ")}`;
  }
  if (dim === "tags") {
    const names   = val.split(",").map((id) => tags.find((t) => String(t.id) === id)?.name ?? id).join(", ");
    const opLabel = op === "all" ? "all of" : op === "none" ? "none of" : "any of";
    return `Tags ${opLabel}: ${names}`;
  }
  if (def?.valueType === "multi-pill") {
    const names = val.split(",").map((v) => def.options.find((o) => o.value === v)?.label ?? v).join(", ");
    return `${label} = ${names}`;
  }
  if (def?.valueType === "daterange") {
    const relOps = { this_week: "This week", this_month: "This month", this_quarter: "This quarter", this_year: "This year" };
    if (relOps[op]) return `Date: ${relOps[op]}`;
    if (op === "year")  return `Date: year ${val}`;
    if (op === "month") return `Date: ${val}`;
    const [from, to] = val.split("|");
    if (from && to) return `Date: ${from} – ${to}`;
    if (from) return `Date from ${from}`;
    if (to)   return `Date to ${to}`;
  }
  if (def?.valueType === "numrange") {
    const [from, to] = val.split("|");
    const base = label.replace(" range", "");
    if (from && to) return `${label}: ${from} – ${to}`;
    if (from) return `${base} ≥ ${from}`;
    if (to)   return `${base} ≤ ${to}`;
  }
  if (def?.options) return `${label} = ${def.options.find((o) => o.value === val)?.label ?? val}`;
  return `${label} = ${val}`;
}
